import { RichtextStoryblok } from '@/lib/types/component-types-sb'
import { buildStoryblokImageUrl, cn, removeColorProperty, slugify } from '@/lib/utils'
import { StoryblokServerComponent } from '@storyblok/react/rsc'
import Image from 'next/image'
import { ComponentProps, isValidElement, JSX, PropsWithChildren, ReactElement } from 'react'
import {
  MARK_CODE,
  NODE_CODEBLOCK,
  NODE_HEADING,
  NODE_IMAGE,
  NODE_LI,
  render,
} from 'storyblok-rich-text-react-renderer'
import { CodeBlock } from './code-block'

type RichTextProps = {
  content?: RichtextStoryblok
  linksToHeadings?: boolean
} & Omit<ComponentProps<'div'>, 'content'>

export function RichText({ content, className, linksToHeadings = false, ...props }: RichTextProps) {
  return (
    <div
      className={cn('prose max-w-full', className)}
      {...props}
    >
      {render(removeColorProperty(content), {
        markResolvers: {
          [MARK_CODE]: (children) => {
            return (
              <code className="bg-muted not-prose rounded-md px-1.5 py-0.5 text-sm font-semibold">
                {children}
              </code>
            )
          },
        },
        nodeResolvers: {
          [NODE_HEADING]: (children, props) => {
            const text = (children as any[])?.join('') || ''
            const slug = slugify(text)
            const Tag = `h${props.level}` as keyof JSX.IntrinsicElements

            return (
              <Tag
                id={slug}
                className="scroll-mt-24"
              >
                {children}
              </Tag>
            )
          },
          [NODE_CODEBLOCK]: (children, props) => {
            const language = props.class ? props.class.split('-').at(1) : 'typescript'

            return (
              <CodeBlock
                language={language}
                className={props.class}
              >
                {children}
              </CodeBlock>
            )
          },
          [NODE_IMAGE]: (children, props) => {
            if (!props.src) return null

            const optimizedSrc = buildStoryblokImageUrl(props.src, {
              width: 1200,
            })

            return (
              <span className="bg-sidebar border-border my-3 block rounded-lg border p-2">
                <Image
                  unoptimized={props.src.includes('svg')}
                  src={optimizedSrc}
                  alt={props.alt || ''}
                  width={1200}
                  height={600}
                  className="not-prose h-auto w-full rounded"
                />
              </span>
            )
          },

          [NODE_LI]: (children) => {
            // Check if the first child is a p tag and unwrap it
            if (
              Array.isArray(children) &&
              children.length === 1 &&
              isValidElement(children[0]) &&
              children[0].type === 'p'
            ) {
              const element = children[0] as ReactElement<PropsWithChildren>
              return <li>{element.props.children}</li>
            }

            return <li>{children}</li>
          },
        },
        defaultBlokResolver: (name, props) => {
          const blok = { ...props, component: name }
          return (
            <StoryblokServerComponent
              blok={blok}
              key={props._uid}
            />
          )
        },
      })}
    </div>
  )
}
